import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const SuccessDialog = ({ isOpen, onClose, message }) => {
    const { language } = useLanguage();
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsAnimating(true);
            // Prevent body scroll when dialog is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div 
                className="success-dialog-backdrop"
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 9998,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'fadeIn 0.3s ease-in-out'
                }}
            >
                {/* Dialog */}
                <div 
                    id="success-modal-container"
                    className="success-dialog-content"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '20px',
                        padding: '40px',
                        maxWidth: '500px',
                        width: '90%',
                        position: 'relative',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        animation: isAnimating ? 'slideUpScale 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
                        transform: isAnimating ? 'scale(1)' : 'scale(0.8)',
                        opacity: isAnimating ? 1 : 0
                    }}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: 'transparent',
                            border: 'none',
                            fontSize: '24px',
                            color: '#999',
                            cursor: 'pointer',
                            padding: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.color = '#333';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#999';
                        }}
                    >
                        <FaTimes />
                    </button>

                    {/* Success Icon */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both',
                            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)'
                        }}>
                            <FaCheckCircle 
                                style={{
                                    fontSize: '48px',
                                    color: '#ffffff',
                                    animation: 'checkmarkDraw 0.6s ease-in-out 0.4s both'
                                }}
                            />
                        </div>
                    </div>

                    {/* Success Message */}
                    <h3 style={{
                        textAlign: 'center',
                        margin: '0 0 15px 0',
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#1f2937',
                        animation: 'fadeInUp 0.5s ease-out 0.3s both'
                    }}>
                        {language === 'pt' ? 'Enviado com Sucesso!' : 'Successfully Sent!'}
                    </h3>

                    <p style={{
                        textAlign: 'center',
                        margin: '0',
                        fontSize: '16px',
                        color: '#6b7280',
                        lineHeight: '1.6',
                        animation: 'fadeInUp 0.5s ease-out 0.4s both'
                    }}>
                        {message}
                    </p>

                    {/* Action Button */}
                    <div style={{
                        marginTop: '30px',
                        display: 'flex',
                        justifyContent: 'center',
                        animation: 'fadeInUp 0.5s ease-out 0.5s both'
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                backgroundColor: '#667eea',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 32px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#5568d3';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#667eea';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                            }}
                        >
                            {language === 'pt' ? 'Fechar' : 'Close'}
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes slideUpScale {
                    from {
                        transform: translateY(30px) scale(0.9);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0) scale(1);
                        opacity: 1;
                    }
                }

                @keyframes scaleIn {
                    from {
                        transform: scale(0);
                        opacity: 0;
                    }
                    to {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                @keyframes checkmarkDraw {
                    from {
                        transform: scale(0) rotate(-45deg);
                        opacity: 0;
                    }
                    to {
                        transform: scale(1) rotate(0deg);
                        opacity: 1;
                    }
                }

                @keyframes fadeInUp {
                    from {
                        transform: translateY(10px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                :global(body.active-dark-mode .success-dialog-content) {
                    background-color: #1f2937 !important;
                }

                :global(body.active-dark-mode .success-dialog-content h3) {
                    color: #ffffff !important;
                }

                :global(body.active-dark-mode .success-dialog-content p) {
                    color: #d1d5db !important;
                }
            `}</style>
        </>
    );
};

export default SuccessDialog;

